import { useEffect } from 'react';
import { Network } from '@capacitor/network';
import showToast from '@/components/ui/Toast';
import { isNativePlatform } from '@/utils/capacitor';

export const OFFLINE_MESSAGE =
  'Anda sedang offline. Aplikasi membutuhkan koneksi internet untuk berfungsi.';
export const ONLINE_MESSAGE = 'Koneksi internet kembali.';

const NETWORK_TOAST_ID = 'network-status';
const DEFAULT_STABLE_MS = 1500;
const DEFAULT_WARMUP_MS = 4000;

type MonitorOptions = {
  stableMs?: number;
  warmupMs?: number;
  now?: () => number;
};

/**
 * Debounce status jaringan dan hanya toast "online" setelah sempat offline secara stabil.
 * State module-level: StrictMode / listener ganda tidak mereset transisi.
 */
export function createConnectionMonitor(options: MonitorOptions = {}) {
  const stableMs = options.stableMs ?? DEFAULT_STABLE_MS;
  const warmupMs = options.warmupMs ?? DEFAULT_WARMUP_MS;
  const now = options.now ?? Date.now;
  const startedAt = now();

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stableConnected: boolean | null = null;

  const flush = (connected: boolean) => {
    if (stableConnected === connected) {
      return;
    }

    const previous = stableConnected;
    stableConnected = connected;

    if (!connected) {
      showToast.error(OFFLINE_MESSAGE, { id: NETWORK_TOAST_ID });
      return;
    }

    const inWarmup = now() - startedAt < warmupMs;
    if (previous === false && !inWarmup) {
      showToast.success(ONLINE_MESSAGE, { id: NETWORK_TOAST_ID });
    }
  };

  return {
    notify(connected: boolean) {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
        flush(connected);
      }, stableMs);
    },
    dispose() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

const connectionMonitor = createConnectionMonitor();

/**
 * Mendeteksi status online/offline dan menampilkan toast.
 * PWA ini tidak offline-capable; hook ini memberi feedback jelas ke pengguna.
 */
export function useOfflineDetection() {
  useEffect(() => {
    if (isNativePlatform()) {
      let cancelled = false;
      let listenerHandle: { remove: () => Promise<void> | void } | null = null;

      const setupNative = async () => {
        try {
          const status = await Network.getStatus();
          if (cancelled) return;
          connectionMonitor.notify(status.connected);

          const handle = await Network.addListener('networkStatusChange', (networkStatus) => {
            connectionMonitor.notify(networkStatus.connected);
          });
          if (cancelled) {
            await handle.remove();
            return;
          }
          listenerHandle = handle;
        } catch (error) {
          console.error('[Network] Failed to setup native listener:', error);
        }
      };

      void setupNative();

      return () => {
        cancelled = true;
        void listenerHandle?.remove();
      };
    }

    connectionMonitor.notify(navigator.onLine);

    const handleOffline = () => connectionMonitor.notify(false);
    const handleOnline = () => connectionMonitor.notify(true);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
}
